import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CONFIG } from '../config/config';
import { CalendarDays, Download, Loader2, RotateCcw } from 'lucide-react';
import { showTime } from '../utils/dateHelpers';
import { toISODateLocal } from '../utils/dateHelpers';
import { useUser } from '../context/UserContext';

export default function AdminSortiePanel({ refreshKey = 0, openReintegrate }) {
  const API = (CONFIG.API_URL || '/api').replace(/\/+$/, '');
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const { etablissement } = useUser();

  const todayLocal = toISODateLocal(new Date());
  const [from, setFrom] = useState(() => todayLocal);
  const [to, setTo] = useState(() => todayLocal);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false); // coupe les retry si 404
  const abortRef = useRef(null);

  const headers = useMemo(() => ({ Authorization: token ? `Bearer ${token}` : '' }), [token]);

  // ---- helpers période
  const fmt = (d) => d.toISOString().slice(0, 10);
  const setRange = (a, b) => {
    setFrom(fmt(a));
    setTo(fmt(b));
  };

  const todayBounds = () => {
    const d = new Date();
    const start = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    return [start, start];
  };
  const weekBounds = () => {
    const now = new Date();
    const day = (now.getDay() + 6) % 7; // lundi=0
    const monday = new Date(now);
    monday.setDate(now.getDate() - day);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return [monday, sunday];
  };
  const monthBounds = () => {
    const now = new Date();
    return [
      new Date(now.getFullYear(), now.getMonth(), 1),
      new Date(now.getFullYear(), now.getMonth() + 1, 0),
    ];
  };

  const setToday = () => setRange(...todayBounds());
  const setThisWeek = () => setRange(...weekBounds());
  const setThisMonth = () => setRange(...monthBounds());

  const isRange = (a, b) => from === fmt(a) && to === fmt(b);
  const isTodayRange = isRange(...todayBounds());
  const isWeekRange = isRange(...weekBounds());
  const isMonthRange = isRange(...monthBounds());

  // ---- fetch
  const fetchRows = useCallback(async () => {
    if (unavailable) return; // si 404 une fois, on n’insiste pas
    abortRef.current?.abort?.();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    try {
      const etabParam = etablissement?.etablissement_id
        ? `&etablissement_id=${encodeURIComponent(etablissement.etablissement_id)}`
        : '';
      const url = `${API}/exports/sorties?from=${from}&to=${to}${etabParam}`;
      const r = await fetch(url, { headers, signal: ctrl.signal });
      if (r.status === 404) {
        setUnavailable(true);
        setRows([]);
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      const rows = Array.isArray(data)
        ? data
        : Array.isArray(data.items)
          ? data.items
          : Array.isArray(data.rows)
            ? data.rows
            : [];
      setRows(rows);
    } catch (e) {
      if (e.name !== 'AbortError') setRows([]);
    } finally {
      setLoading(false);
    }
  }, [API, from, to, headers, unavailable, etablissement]);

  useEffect(() => {
    fetchRows();
    return () => abortRef.current?.abort?.();
  }, [fetchRows, refreshKey]);

  // ---- filtrage/agrégats
  const finalizedOrArchived = useMemo(
    () =>
      rows.filter((r) => {
        const step = Number(r.currentStep ?? r.currentstep ?? r.current_step);
        const s = String(r.statut || '').toLowerCase();
        return step >= 8 || step === 999 || s.startsWith('archiv');
      }),
    [rows],
  );

  const stats = useMemo(() => {
    const totals = finalizedOrArchived
      .map((r) => Number(r.cotationTotal ?? r.cotation_total))
      .filter((n) => Number.isFinite(n));
    const count = finalizedOrArchived.length;
    const sum = totals.reduce((a, b) => a + b, 0);
    const avg = totals.length ? sum / totals.length : 0;
    return { count, sum, avg, countWithTotal: totals.length };
  }, [finalizedOrArchived]);

  // ---- UI
  if (unavailable) {
    return (
      <div className="px-2 py-2 text-sm text-slate-500">
        Le récap “Sorties (période)” n’est pas disponible sur cet environnement.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header + filtres + stats */}
      <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-indigo-50 to-sky-50 px-4 py-3">
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr_auto] items-center gap-3">
            <LabeledDate label="Du" value={from} onChange={setFrom} />
            <span className="hidden lg:block text-slate-400">→</span>
            <LabeledDate label="Au" value={to} onChange={setTo} />
            <button
              onClick={fetchRows}
              className="justify-self-end inline-flex items-center gap-2 px-3 py-2 rounded border border-slate-300 bg-white hover:bg-slate-50"
              title="Recharger la période affichée"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4" />
              )}
              Recharger
            </button>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex flex-wrap gap-2">
              <Chip onClick={setToday} selected={isTodayRange}>
                Aujourd’hui
              </Chip>
              <Chip onClick={setThisWeek} selected={isWeekRange}>
                Cette semaine
              </Chip>
              <Chip onClick={setThisMonth} selected={isMonthRange}>
                Ce mois
              </Chip>
            </div>

            <div className="md:ml-auto grid grid-cols-5 gap-2 min-w-[260px]">
              <div className="hidden md:col-span-3 md:inline-block"></div>
              <div className="md:col-span-2">
                <Stat label="Patients" value={String(stats.count)} color="indigo"/>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Liste */}
      <div className="space-y-3">
        {loading && finalizedOrArchived.length === 0 ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        ) : finalizedOrArchived.length === 0 ? (
          <div className="italic text-slate-500">Aucune sortie sur la période.</div>
        ) : (
          finalizedOrArchived.map((r) => {
            const totalRaw = r.cotationTotal ?? r.cotation_total;
            const total = Number.isFinite(Number(totalRaw)) ? Number(totalRaw) : null;
            const uid = r.passage_uid;
            return (
              <article key={r.id} className="border rounded-xl bg-white p-4 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="min-w-0">
                    <div className=" truncate">
                      <span className="text-[15px] font-semibold text-slate-900">{r.prenom}</span>
                      &nbsp;
                      <span className="text-[15px] font-semibold text-slate-900 uppercase">
                        {r.nom}
                      </span>
                      &nbsp;
                      <span className="text-[13px] font-light text-slate-900 uppercase">{uid}</span>
                    </div>
                    <div className="text-[12px] text-slate-500">
                      Entrée {showTime(r.heure_entree)} • Sortie {showTime(r.heure_sortie) || '—'}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {total != null && (
                      <span className="inline-flex items-center bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded text-[12px] font-semibold">
                        {total.toFixed(2)} €
                      </span>
                    )}
                    <button
                      onClick={() => downloadPdf(API, headers, r)}
                      className="inline-flex items-center gap-2 px-2.5 py-1.5 border rounded text-xs hover:bg-slate-50"
                      title="Télécharger le PDF"
                    >
                      <Download className="w-4 h-4" />
                      PDF
                    </button>
                    <button
                      onClick={() => openReintegrate(r)}
                      className="inline-flex items-center gap-2 px-2.5 py-1.5 border rounded text-xs bg-red-600 text-white"
                      title="Réintégrer"
                      aria-label="Réintégrer"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Réintégrer
                    </button>
                  </div>
                </div>
              </article>
            );
          })
        )}
      </div>
    </div>
  );
}

function downloadPdf(API, headers, row) {
  // 🔖 Nom de fichier: patient_<MusiqID>_Nom_Prenom.pdf (fallback: Nom_Prenom.pdf)
  const uid = row.passage_uid;
  const safe = (s) =>
    String(s || '')
      .normalize('NFKD')
      .replace(/[^\w.-]+/g, '-');
  const base = `${safe(row.nom)}_${safe(row.prenom)}`;
  const filename = uid ? `patient_${uid}_${base}.pdf` : `${base}.pdf`;
  fetch(`${API}/exports/patient/${row.id}/pdf`, { headers })
    .then(async (res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    })
    .catch((e) => console.error('[AdminSortiePanel] PDF', e?.message || e));
}

function Chip({ children, onClick, selected = false }) {
  return (
    <button
      onClick={onClick}
      className={[
        'px-3 py-1.5 text-sm rounded-full border',
        selected
          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
          : 'border-slate-300 hover:bg-slate-50',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function Stat({ label, value, color = 'indigo' }) {
  const tint = {
    indigo: ['bg-indigo-50 text-indigo-700'],
    emerald: ['bg-emerald-50 text-emerald-700'],
    sky: ['bg-sky-50 text-sky-700'],
  }[color] || ['bg-slate-50 text-slate-700'];
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-sm font-semibold">
        <span className={`inline-flex px-2 py-0.5 rounded ${tint[0]}`}>{value}</span>
      </div>
    </div>
  );
}

function LabeledDate({ label, value, onChange }) {
  return (
    <label className="flex items-center gap-2">
      <span className="text-xs font-semibold text-slate-600">{label}</span>
      <div className="flex items-center gap-2 border rounded px-2 py-1 bg-white">
        <CalendarDays className="w-4 h-4 text-slate-500" />
        <input
          type="date"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          className="px-1 py-1 outline-none"
        />
      </div>
    </label>
  );
}
